import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Activity, FileSpreadsheet, Fence, Gauge, MapPinned, Radio, Route, Settings, ShieldCheck, Thermometer, Timer, Truck, Waypoints,
  UserCircle, Users, Workflow,
} from 'lucide-react'
import { sessionDisplayName, useSession } from '../auth/useSession'
import { defaultSurfaceFor, fleetPills, pathFor, surfacePills, TopBar, type FleetId } from '../cc'
import { CC } from '../cc/tokens'
import { reportCard, reportSidebarActive } from '../reports/ui'
import { fetchAdminStatus } from './useAdminApi'
import type { SettingsSection } from './types'
import { AccessSection } from './AccessSection'
import { BatteryOverheatReportSection } from './BatteryOverheatReportSection'
import { DarReportSection } from './DarReportSection'
import { GeofencesSection } from './GeofencesSection'
import { HealthSection } from './HealthSection'
import { TracesSection } from './TracesSection'
import { ProfileSection } from './ProfileSection'
import { SessionsSection } from './SessionsSection'
import { StagesSection } from './StagesSection'
import { TatSection } from './TatSection'
import { SpeedSection } from './SpeedSection'
import { BoundaryMapSection, ThresholdsSection } from './ThresholdsSection'
import { UsersSection } from './UsersSection'
import { VehiclesSection } from './VehiclesSection'

const NAV: {
  id: SettingsSection; label: string; icon: typeof Truck; hint: string; superAdminOnly?: boolean
}[] = [
  { id: 'vehicles', label: 'Vehicles', icon: Truck, hint: 'Fleet roster and crew' },
  { id: 'tat', label: 'TAT targets', icon: Timer, hint: 'Turnaround SLAs' },
  { id: 'thresholds', label: 'Thresholds', icon: Gauge, hint: 'Live alert knobs' },
  { id: 'speed', label: 'Speed profile', icon: Route, hint: 'Per-km limit in force, by route' },
  { id: 'boundary', label: 'Boundary map', icon: MapPinned, hint: 'TIMS → stage phases' },
  { id: 'stages', label: 'Stages', icon: Workflow, hint: 'Pipeline topology' },
  { id: 'geofences', label: 'Geofences', icon: Fence, hint: 'Fence definitions' },
  // Server-enforced too (403 on every /api/auth/users route for anyone else) -- filtered
  // here as a courtesy, same rule useSession.ts states: hiding a control is not the rule.
  { id: 'users', label: 'Users', icon: Users, hint: 'Accounts, roles, access', superAdminOnly: true },
  { id: 'access', label: 'Access', icon: ShieldCheck, hint: 'Department & site grants', superAdminOnly: true },
  {
    id: 'health', label: 'System health', icon: Activity,
    hint: 'Pod restarts, crash-loops, pipeline staleness', superAdminOnly: true,
  },
  {
    id: 'dar', label: 'DAR report', icon: FileSpreadsheet,
    hint: 'Daily Alerts Report, every site', superAdminOnly: true,
  },
  {
    id: 'traces', label: 'Packet traces', icon: Waypoints,
    hint: 'One packet, bridge to reporter', superAdminOnly: true,
  },
  {
    id: 'battery-overheat', label: 'Battery overheat', icon: Thermometer,
    hint: 'Temperature report, MBMT/Nagpur/GTI/JM Baxi', superAdminOnly: true,
  },
  { id: 'sessions', label: 'Sessions', icon: Radio, hint: 'Who is online, and the sign-in history', superAdminOnly: true },
  { id: 'profile', label: 'Your account', icon: UserCircle, hint: 'Change your password' },
]


const SETTINGS_SECTIONS = new Set<string>([
  'vehicles', 'tat', 'stages', 'geofences', 'thresholds', 'speed', 'boundary', 'users',
  'access', 'health', 'dar', 'battery-overheat', 'sessions', 'traces', 'profile',
])

export function SettingsShell() {
  const navigate = useNavigate()
  const routeParams = useParams<{ section?: string }>()
  const session = useSession()
  // An unknown/missing segment reads as the default rather than a blank page -- same
  // reasoning as ReportsShell's mode fallback.
  const section: SettingsSection = (
    routeParams.section && SETTINGS_SECTIONS.has(routeParams.section)
      ? routeParams.section : 'vehicles'
  ) as SettingsSection
  const setSection = (s: SettingsSection): void => navigate(`/settings/${s}`)
  const [configured, setConfigured] = useState(false)
  const [clock, setClock] = useState(() =>
    new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  )

  useEffect(() => {
    const id = window.setInterval(() => {
      setClock(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
    }, 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    void fetchAdminStatus().then((s) => setConfigured(s.configured))
  }, [])

  const isSuperAdmin = session.status === 'signed-in' && session.session.role === 'super_admin'
  const nav = NAV.filter((n) => !n.superAdminOnly || isSuperAdmin)
  // A non-super-admin who somehow lands on the users/access tab (a stale link, a role that
  // just got downgraded) falls back to the one section everyone can reach, rather than a
  // blank pane the filtered nav can no longer even point them away from.
  const isSuperAdminOnlySection = section === 'users' || section === 'access' || section === 'health'
    || section === 'dar' || section === 'battery-overheat' || section === 'sessions' || section === 'traces'
  const activeSection = isSuperAdminOnlySection && !isSuperAdmin ? 'vehicles' : section

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3" style={{ background: CC.page }}>
      <TopBar
        clock={clock}
        live={configured}
        user={sessionDisplayName(session)}
        fleets={fleetPills(isSuperAdmin)}
        activeFleet="ultratech"
        onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
        nav={surfacePills('ultratech')}
        onNav={(id) => navigate(pathFor('ultratech', id === 'reports' ? 'reports' : 'command'))}
        onSettings={() => navigate('/settings')}
        settingsActive
      />

      <div className="flex min-h-0 flex-1 gap-3 overflow-hidden">
        <aside className={clsx(reportCard, 'flex w-[220px] shrink-0 flex-col overflow-hidden')}>
          <div className="border-b border-slate-200/80 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Settings size={16} className="text-slate-500" />
              Settings
            </div>
            <p className="mt-0.5 text-xs text-slate-500">Master data management</p>
          </div>
          <nav className="flex-1 overflow-auto p-2">
            {nav.map((n) => {
              const Icon = n.icon
              const on = activeSection === n.id
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => setSection(n.id)}
                  className={clsx(
                    'mb-0.5 flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    on ? reportSidebarActive : 'text-slate-600 hover:bg-slate-50',
                  )}
                >
                  <Icon size={15} className="mt-0.5 shrink-0 opacity-70" />
                  <span>
                    <span className="block font-medium">{n.label}</span>
                    <span className="text-xs text-slate-400">{n.hint}</span>
                  </span>
                </button>
              )
            })}
          </nav>
        </aside>

        <main className={clsx(reportCard, 'min-w-0 flex-1 overflow-auto p-6')}>
          {activeSection === 'vehicles' ? <VehiclesSection configured={configured} /> : null}
          {activeSection === 'tat' ? <TatSection configured={configured} /> : null}
          {activeSection === 'thresholds' ? <ThresholdsSection configured={configured} /> : null}
          {activeSection === 'speed' ? <SpeedSection configured={configured} /> : null}
          {activeSection === 'boundary' ? <BoundaryMapSection configured={configured} /> : null}
          {activeSection === 'stages' ? <StagesSection /> : null}
          {activeSection === 'geofences' ? <GeofencesSection configured={configured} /> : null}
          {activeSection === 'users' ? (
            <UsersSection
              isSuperAdmin={isSuperAdmin}
              currentUsername={session.status === 'signed-in' ? (session.session.username ?? '') : ''}
            />
          ) : null}
          {activeSection === 'access' ? <AccessSection /> : null}
          {activeSection === 'health' ? <HealthSection isSuperAdmin={isSuperAdmin} /> : null}
          {activeSection === 'traces' ? <TracesSection isSuperAdmin={isSuperAdmin} /> : null}
          {activeSection === 'dar' ? <DarReportSection isSuperAdmin={isSuperAdmin} /> : null}
          {activeSection === 'battery-overheat' ? <BatteryOverheatReportSection isSuperAdmin={isSuperAdmin} /> : null}
          {activeSection === 'sessions' ? <SessionsSection isSuperAdmin={isSuperAdmin} /> : null}
          {activeSection === 'profile' && session.status === 'signed-in' ? (
            <ProfileSection session={session.session} />
          ) : null}
        </main>
      </div>
    </div>
  )
}
